import { useState, useEffect, useRef } from "react";
import {
  createTradingSession,
  updateTradingSession,
  getActiveTradingSession,
  useQuery,
  useAction,
} from "wasp/client/operations";
import { Button } from "../client/components/ui/button";
import { Input } from "../client/components/ui/input";
import { Label } from "../client/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../client/components/ui/select";
import { Textarea } from "../client/components/ui/textarea";
import { toast } from "../client/hooks/use-toast";
import { Play } from "lucide-react";
import useTranslation from "../client/hooks/useTranslation";
import { trackSessionStarted } from '../client/utils/posthog';

// Contrôleur de la session minutée (démarrer, pause, arrêter, évaluer), sorti
// de SessionCard pour que le mode Lite pilote la MÊME logique avec son propre
// rendu. SessionCard (widget « session » de l'Advanced) l'utilise tel quel.

type MoodValue = "excellent" | "good" | "neutral" | "poor" | "bad";

interface SessionEvaluationForm {
  mood: MoodValue | "";
  notes: string;
  mentalState: string;
  performance: "excellent" | "good" | "average" | "poor" | "";
  maxRunup: string;
  maxDrawdown: string;
}

const initialFormData: SessionEvaluationForm = {
  mood: "",
  notes: "",
  mentalState: "",
  performance: "",
  maxRunup: "",
  maxDrawdown: "",
};

export const formatTimerTime = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${m}:${String(s).padStart(2, "0")}`;
};

export function useSessionTimer() {
  const t = useTranslation();

  // Pre-session start dialog
  const [isStartDialogOpen, setIsStartDialogOpen] = useState(false);
  const [preMood, setPreMood] = useState<MoodValue | "">("");
  const [plannedDuration, setPlannedDuration] = useState(120);
  const [noTimer, setNoTimer] = useState(false);

  // Post-session evaluation dialog
  const [isEvaluationDialogOpen, setIsEvaluationDialogOpen] = useState(false);
  const [evaluationForm, setEvaluationForm] = useState<SessionEvaluationForm>(initialFormData);

  // Timer state
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const pausedTimeRef = useRef<number>(0);
  const pauseStartRef = useRef<number | null>(null);

  const { data: activeSession, isLoading, refetch } = useQuery(getActiveTradingSession);
  const createSessionFn = useAction(createTradingSession);
  const updateSessionFn = useAction(updateTradingSession);

  const hasTimer = activeSession?.isActive && activeSession?.plannedDuration != null;

  useEffect(() => {
    if (activeSession && activeSession.isActive && activeSession.startedAt) {
      const startTime = new Date(activeSession.startedAt).getTime();

      const updateTimer = () => {
        if (isPaused) return;
        const now = Date.now();
        const elapsed = now - startTime - pausedTimeRef.current;
        setElapsedSeconds(Math.floor(elapsed / 1000));

        if (hasTimer) {
          const plannedDurationMs = activeSession.plannedDuration! * 60 * 1000;
          const remaining = Math.max(0, plannedDurationMs - elapsed);
          setTimeRemaining(Math.floor(remaining / 1000));
          if (remaining <= 0) handleSessionEnd();
        }
      };

      updateTimer();
      intervalRef.current = setInterval(updateTimer, 1000);
      return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
    } else {
      setTimeRemaining(null);
      setElapsedSeconds(0);
      if (intervalRef.current) clearInterval(intervalRef.current);
    }
  }, [activeSession, isPaused, hasTimer]);

  // Open the pre-session dialog instead of starting directly
  const handleOpenStartDialog = () => {
    setPreMood("");
    setIsStartDialogOpen(true);
  };

  const handleConfirmStart = async () => {
    try {
      await createSessionFn({
        plannedDuration: noTimer ? null : plannedDuration,
        mood: preMood || null,
      });
      trackSessionStarted();
      toast({
        title: t.session.started,
        description: noTimer
          ? t.session.startedNoTimerDescription
          : `${t.session.title} de ${plannedDuration} ${t.common.minutes} lancée.`,
      });
      setIsStartDialogOpen(false);
      refetch();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || "Impossible de démarrer la session.",
        variant: "destructive",
      });
    }
  };

  const handlePauseResume = () => {
    if (!activeSession) return;
    if (isPaused) {
      if (pauseStartRef.current) {
        pausedTimeRef.current += Date.now() - pauseStartRef.current;
        pauseStartRef.current = null;
      }
      setIsPaused(false);
    } else {
      pauseStartRef.current = Date.now();
      setIsPaused(true);
    }
  };

  const handleStopSession = () => {
    if (!activeSession) return;
    // Pre-fill post-session mood with pre-session mood if not yet set
    setEvaluationForm((prev) => ({
      ...prev,
      mood: prev.mood || (activeSession as any).mood || "",
    }));
    setIsEvaluationDialogOpen(true);
  };

  const handleSessionEnd = () => {
    if (!activeSession) return;
    setEvaluationForm((prev) => ({
      ...prev,
      mood: prev.mood || (activeSession as any).mood || "",
    }));
    setIsEvaluationDialogOpen(true);
  };

  const endSession = async (withEvaluation: boolean) => {
    if (!activeSession) return;
    const startTime = activeSession.startedAt
      ? new Date(activeSession.startedAt).getTime()
      : Date.now();
    const actualDuration = Math.floor((Date.now() - startTime - pausedTimeRef.current) / 60000);

    const updateData: any = {
      id: activeSession.id,
      actualDuration,
      endedAt: new Date(),
      isActive: false,
    };

    if (withEvaluation) {
      if (evaluationForm.mood) updateData.mood = evaluationForm.mood;
      if (evaluationForm.performance) updateData.performance = evaluationForm.performance;
      if (evaluationForm.mentalState) updateData.mentalState = evaluationForm.mentalState;
      if (evaluationForm.notes) updateData.notes = evaluationForm.notes;
      if (evaluationForm.maxRunup !== "") updateData.maxRunup = parseFloat(evaluationForm.maxRunup) || null;
      if (evaluationForm.maxDrawdown !== "") updateData.maxDrawdown = parseFloat(evaluationForm.maxDrawdown) || null;
    }

    await updateSessionFn(updateData);

    toast({ title: t.session.sessionEnded, description: t.session.sessionEndedDescription });

    setIsEvaluationDialogOpen(false);
    setEvaluationForm(initialFormData);
    setTimeRemaining(null);
    setElapsedSeconds(0);
    setIsPaused(false);
    pausedTimeRef.current = 0;
    pauseStartRef.current = null;
    refetch();
  };

  const handleSubmitEvaluation = async () => {
    if (!activeSession) return;
    try {
      await endSession(true);
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message || "Impossible de terminer la session.", variant: "destructive" });
    }
  };

  const handleSkipEvaluation = async () => {
    if (!activeSession) return;
    try {
      await endSession(false);
    } catch (error: any) {
      toast({ title: t.common.error, description: error.message || "Impossible de terminer la session.", variant: "destructive" });
    }
  };

  const dialogs = (
    <>
      {/* Pre-session mood dialog */}
      <Dialog open={isStartDialogOpen} onOpenChange={setIsStartDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t.session.preMoodTitle}</DialogTitle>
            <DialogDescription>{t.session.preMoodDescription}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label htmlFor="pre-mood">{t.session.mood}</Label>
            <Select value={preMood} onValueChange={(v) => setPreMood(v as MoodValue)}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder={t.session.moodPlaceholder} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="excellent">{t.session.moodExcellent}</SelectItem>
                <SelectItem value="good">{t.session.moodGood}</SelectItem>
                <SelectItem value="neutral">{t.session.moodNeutral}</SelectItem>
                <SelectItem value="poor">{t.session.moodPoor}</SelectItem>
                <SelectItem value="bad">{t.session.moodBad}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsStartDialogOpen(false)}>
              {t.common.cancel}
            </Button>
            <Button onClick={handleConfirmStart} disabled={!preMood}>
              <Play className="mr-1 h-3 w-3" />
              {t.session.start}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Post-session evaluation dialog */}
      <Dialog open={isEvaluationDialogOpen} onOpenChange={(open) => { if (!open) handleSkipEvaluation(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.session.evaluation}</DialogTitle>
            <DialogDescription>{t.session.evaluationDescription}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="mood">{t.session.mood} *</Label>
                <Select
                  value={evaluationForm.mood}
                  onValueChange={(v) => setEvaluationForm({ ...evaluationForm, mood: v as MoodValue })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.session.moodPlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">{t.session.moodExcellent}</SelectItem>
                    <SelectItem value="good">{t.session.moodGood}</SelectItem>
                    <SelectItem value="neutral">{t.session.moodNeutral}</SelectItem>
                    <SelectItem value="poor">{t.session.moodPoor}</SelectItem>
                    <SelectItem value="bad">{t.session.moodBad}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="performance">{t.session.performance} *</Label>
                <Select
                  value={evaluationForm.performance}
                  onValueChange={(v) => setEvaluationForm({ ...evaluationForm, performance: v as SessionEvaluationForm["performance"] })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t.session.performancePlaceholder} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="excellent">{t.session.perfExcellent}</SelectItem>
                    <SelectItem value="good">{t.session.perfGood}</SelectItem>
                    <SelectItem value="average">{t.session.perfAverage}</SelectItem>
                    <SelectItem value="poor">{t.session.perfPoor}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="mentalState">{t.session.mentalState}</Label>
              <Input
                id="mentalState"
                value={evaluationForm.mentalState}
                onChange={(e) => setEvaluationForm({ ...evaluationForm, mentalState: e.target.value })}
                placeholder={t.session.mentalStatePlaceholder}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxRunup">{t.session.maxRunup}</Label>
                <Input
                  id="maxRunup"
                  type="number"
                  step="0.01"
                  value={evaluationForm.maxRunup}
                  onChange={(e) => setEvaluationForm({ ...evaluationForm, maxRunup: e.target.value })}
                  placeholder={t.session.maxRunupPlaceholder}
                />
              </div>
              <div>
                <Label htmlFor="maxDrawdown">{t.session.maxDrawdown}</Label>
                <Input
                  id="maxDrawdown"
                  type="number"
                  step="0.01"
                  value={evaluationForm.maxDrawdown}
                  onChange={(e) => setEvaluationForm({ ...evaluationForm, maxDrawdown: e.target.value })}
                  placeholder={t.session.maxDrawdownPlaceholder}
                />
              </div>
            </div>

            <div>
              <Label htmlFor="notes">{t.session.notes}</Label>
              <Textarea
                id="notes"
                value={evaluationForm.notes}
                onChange={(e) => setEvaluationForm({ ...evaluationForm, notes: e.target.value })}
                placeholder={t.session.notesPlaceholder}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleSkipEvaluation}>
              {t.session.skipEvaluation}
            </Button>
            <Button
              onClick={handleSubmitEvaluation}
              disabled={!evaluationForm.mood || !evaluationForm.performance}
            >
              {t.session.endSession}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );

  return {
    activeSession,
    isLoading,
    hasTimer,
    timeRemaining,
    elapsedSeconds,
    isPaused,
    noTimer,
    setNoTimer,
    plannedDuration,
    setPlannedDuration,
    handleOpenStartDialog,
    handlePauseResume,
    handleStopSession,
    refetch,
    /** Fenêtres de démarrage (humeur) et d'évaluation : à rendre une fois. */
    dialogs,
  };
}
